import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useSidebarCollapse } from 'context/SidebarCollapseContext';
import { useIsMobile } from 'hooks/useIsMobile';
import { useFilterRailCollapse } from './useFilterRailCollapse';

/** Search's rail geometry, reused verbatim (spec decision 4). */
export const FILTER_RAIL_WIDTH_PX = 344;
/** The collapsed pip strip (decision 5). */
export const FILTER_STRIP_WIDTH_PX = 48;
/** TradeSidebar's design width; the live width is measured (360px under 1200px). */
export const TRADE_SIDEBAR_WIDTH_PX = 400;
/**
 * Below this content-region width the rail stops pushing and auto-collapses to
 * the pip strip while the cart is open. The one sanctioned overlay case.
 */
export const MIN_CONTENT_WIDTH_PX = 720;
/** Class a page's content region carries so it pushes with the drawers. */
export const DRAWER_PUSH_CLASS = 'drawer-push';
/** Body class while a page has right-hand drawers: the header and banners end at the drawers' edge. */
export const RIGHT_DRAWERS_BODY_CLASS = 'right-drawers';

/**
 * THE reactive values. Everything to the right of the content region reads
 * these: the rail column's width, the cart's slot, the header's and banners'
 * right edge, the content region's padding. One value animates (on the shared
 * --drawer-transition-*); everything else follows it, so nothing can drift.
 */
export const RAIL_WIDTH_VAR = '--rail-width';
export const CART_WIDTH_VAR = '--cart-width';

export type RailMode = 'open' | 'strip' | 'none';

export interface DrawerLayout {
  railMode: RailMode;
  /** Width the rail column occupies right now: 344, 48 or 0. */
  railWidth: number;
  /** Width the cart occupies right now: its live width when open, else 0. */
  cartWidth: number;
  /** The rail is a strip because space ran out, not because the user folded it. */
  autoCollapsed: boolean;
  /** The user's stored preference (useFilterRailCollapse), one global value. */
  collapsedPref: boolean;
  toggleCollapsed: () => void;
  /** Spread onto the content region: `<div {...layout.pushProps}>`. */
  pushProps: { className: string; style?: CSSProperties };
}

interface DrawerLayoutOptions {
  /** The trade cart (TradeSidebar) is open on this page. */
  cartOpen: boolean;
  /** A filter rail is mounted on the current view (false on views without one, or when hidden). */
  railMounted: boolean;
}

/**
 * How many callers are currently publishing the layout values. Only the LAST
 * one to unmount resets them: when a page swaps one tab for another (My Cards
 * Want List -> Have List), the outgoing tab's rail unmounts a frame before the
 * incoming one mounts, and resetting --rail-width to 0 in between made the
 * header slide out and back although the rail stayed open the whole time.
 */
let publisherCount = 0;

const measure = (selector: string, fallback: number): number => {
  const el = document.querySelector<HTMLElement>(selector);
  return el ? el.getBoundingClientRect().width || fallback : fallback;
};

/**
 * ONE rule for both right-hand drawers (filter rail, trade cart), copied from
 * the Search page's rail/header relationship and made reactive: each drawer is
 * a full-height opaque column pinned to the right edge, the header and the
 * content region END where the drawers begin, and every one of those edges
 * reads the same two CSS custom properties this hook writes on <html>.
 *
 * Both drawers push. When the cart is open and the content region would drop
 * under MIN_CONTENT_WIDTH_PX, the rail auto-collapses to the pip strip
 * (animated, not a user state) and from THAT strip a click opens the rail as
 * an overlay with a scrim — the only overlay case. The user's stored collapse
 * preference (one global value, same storage pattern as the left nav's) still
 * wins and is never written by the auto-collapse.
 *
 * Call it from the page (for the content push) and from the rail (for its own
 * mode); same inputs, same answer, nothing prop-drilled. The last mounted
 * caller's values win on <html>, which is fine because every caller on a page
 * computes the same thing.
 */
export const useDrawerLayout = ({ cartOpen, railMounted }: DrawerLayoutOptions): DrawerLayout => {
  const isMobile = useIsMobile();
  const { collapsed: collapsedPref, toggle: toggleCollapsed } = useFilterRailCollapse();
  const { isCollapsed: navCollapsed } = useSidebarCollapse();

  const compute = useCallback(() => {
    const viewport = window.innerWidth;
    const navWidth = measure('.left-navigation-bar', navCollapsed ? 72 : 250);
    // Open: the cart's live width. Collapsed with a trade in progress: its
    // 48px pip strip (TradeSidebar renders it). Otherwise nothing.
    const cartWidth = cartOpen
      ? measure('.trade-sidebar', TRADE_SIDEBAR_WIDTH_PX)
      : document.querySelector('.trade-cart-strip')
        ? FILTER_STRIP_WIDTH_PX
        : 0;
    return { cartWidth, available: viewport - navWidth - cartWidth };
  }, [cartOpen, navCollapsed]);

  const [geometry, setGeometry] = useState(compute);

  useEffect(() => {
    setGeometry(compute());
    const onResize = () => setGeometry(compute());
    window.addEventListener('resize', onResize);
    // The nav and cart animate for --drawer-transition-duration; re-measure once
    // they have settled so the rule reads their final widths.
    const settle = setTimeout(onResize, 320);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(settle);
    };
  }, [compute]);

  const autoCollapsed =
    railMounted && !isMobile && cartOpen && !collapsedPref && geometry.available - FILTER_RAIL_WIDTH_PX < MIN_CONTENT_WIDTH_PX;

  const railMode: RailMode = !railMounted || isMobile ? 'none' : collapsedPref || autoCollapsed ? 'strip' : 'open';
  const railWidth = railMode === 'open' ? FILTER_RAIL_WIDTH_PX : railMode === 'strip' ? FILTER_STRIP_WIDTH_PX : 0;
  const cartWidth = isMobile ? 0 : geometry.cartWidth;

  // Publish the two values where every edge can read them. Reset only when the
  // last publisher on the page leaves (see publisherCount), never on a re-render
  // or a sibling swap, so an open rail staying open never animates the header.
  useEffect(() => {
    publisherCount += 1;
    return () => {
      publisherCount -= 1;
      if (publisherCount === 0) {
        const root = document.documentElement;
        root.style.setProperty(RAIL_WIDTH_VAR, '0px');
        root.style.setProperty(CART_WIDTH_VAR, '0px');
        document.body.classList.remove(RIGHT_DRAWERS_BODY_CLASS);
      }
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(RAIL_WIDTH_VAR, `${railWidth}px`);
    root.style.setProperty(CART_WIDTH_VAR, `${cartWidth}px`);
    if (!isMobile) document.body.classList.add(RIGHT_DRAWERS_BODY_CLASS);
    else document.body.classList.remove(RIGHT_DRAWERS_BODY_CLASS);
  }, [railWidth, cartWidth, isMobile]);

  return {
    railMode,
    railWidth,
    cartWidth,
    autoCollapsed,
    collapsedPref,
    toggleCollapsed,
    pushProps: { className: DRAWER_PUSH_CLASS },
  };
};

export default useDrawerLayout;
